import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { SparklesIcon } from '@heroicons/react/solid'

import classNames from '@/utils/classNamesLocal'
import { useGlobalState } from '@/state'
import Modal from '@/shared/Modal'
import useModal from '@/hooks/useModalV2'
import useQuery from '@/hooks/useQuery'
import PrimaryButton from '@/shared/Buttons/Primary'
import DefaultButton from '@/shared/Buttons/Default'
import ImageUploadFormR2 from '@/shared/ImageUploadFormR2'

const defaultImages = [
  { name: 'waves', url: 'https://images.castingcall.club/covers/cover-cheese.svg' },
  { name: 'confetti-blue', url: 'https://images.castingcall.club/covers/cover-confetti-blue.svg' },
  { name: 'confetti-orange', url: 'https://images.castingcall.club/covers/cover-confetti-orange.svg' },
  { name: 'diamond-blue', url: 'https://images.castingcall.club/covers/cover-diamond-blue-orange.svg' },
  { name: 'diamond-purple', url: 'https://images.castingcall.club/covers/cover-diamond-blue-purple.svg' },
  { name: 'dalmation-purple', url: 'https://images.castingcall.club/covers/cover-dalmation-purple.svg' },
  { name: 'dalmation', url: 'https://images.castingcall.club/covers/cover-dalmation.svg' },
  { name: 'mountains', url: 'https://images.castingcall.club/covers/cover-mountains.svg' },
  { name: 'slanted', url: 'https://images.castingcall.club/covers/cover-slanted.svg' },
  { name: 'bullseye', url: 'https://images.castingcall.club/covers/cover-bullseye.svg' }
]

const ProfileGeneralCover = () => {
  const [user, setUser] = useGlobalState('userContainerUser')
  const [currentUser] = useGlobalState('currentUser')
  const [url, setUrl] = useState(user.coverPublicImageUrl)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('preset')
  const { openModal, closeModal, isOpen } = useModal()
  const { putpostRequest } = useQuery()

  const isPremium = !!currentUser?.gold

  const save = () => {
    setLoading(true)
    const data = {
      profile: {
        public_cover_image_url: url
      }
    }
    putpostRequest(`/api/v3/profiles/${user.profileId}`, 'PATCH', data, (err, jsonData) => {
      setLoading(false)
      if (err) { return }
      closeModal()

      setUser({ ...user, coverPublicImageUrl: url })
    })
  }

  const onCustomUploadUrl = (newUrl) => {
    // Immediate preview update. The profile row is updated server-side
    // by ProcessImageUploadsJob once processing finishes.
    setUser({ ...user, coverPublicImageUrl: newUrl })
    setUrl(newUrl)
  }

  const onCustomUploadSuccess = () => {
    closeModal()
  }

  return <>
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Cover photo</label>
      <div onClick={openModal} className="mt-1 hover:opacity-75 cursor-pointer flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
        <div className='overflow-hidden aspect-w-3 aspect-h-1 w-full'>
          <img src={user.coverPublicImageUrl} className='object-cover' />
        </div>
      </div>
      <Modal full isOpen={isOpen} closeModal={closeModal}>
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-3">Edit Cover Photo</h3>

        {/* Tabs */}
        <div className='border-b border-gray-200 dark:border-gray-700 mb-4'>
          <nav className='-mb-px flex space-x-6' aria-label='Tabs'>
            <button
              type='button'
              onClick={() => setActiveTab('preset')}
              className={classNames(
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm',
                activeTab === 'preset'
                  ? 'border-cccpurple text-cccpurple'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              )}
            >
              Choose a design
            </button>
            <button
              type='button'
              onClick={() => setActiveTab('upload')}
              className={classNames(
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1',
                activeTab === 'upload'
                  ? 'border-cccpurple text-cccpurple'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              )}
            >
              Upload your own
              <SparklesIcon className='h-4 w-4 text-yellow-500' aria-label='Premium feature' />
            </button>
          </nav>
        </div>

        {activeTab === 'preset' && (
          <>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md">
              <div className='overflow-hidden aspect-w-3 aspect-h-1 w-full'>
                <img src={url} className='object-cover' />
              </div>
            </div>
            <ul role="list" className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8 mt-4">
              {defaultImages.map((img) => (
                <li key={img.name} onClick={() => setUrl(img.url)} className="relative">
                  <div className="group block w-full aspect-w-3 aspect-h-1 rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-gray-100 focus-within:ring-indigo-500 overflow-hidden">
                    <img src={img.url} alt="" className="object-cover pointer-events-none group-hover:opacity-75" />
                    <button type="button" className="absolute inset-0 focus:outline-none">
                      <span className="sr-only" hidden>{img.name}</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className='flex justify-end mt-5'>
              <PrimaryButton loading={loading} onClick={save} text="Save" />
              <DefaultButton className='ml-5' onClick={closeModal} text="Cancel" />
            </div>
          </>
        )}

        {activeTab === 'upload' && isPremium && (
          <>
            <p className='text-sm text-gray-600 dark:text-gray-300 mb-3'>
              Upload a 16:9 image — a crop tool will help you frame it. Processing takes about a minute after you save.
            </p>
            <ImageUploadFormR2
              kind='ProfileCover'
              objectId={user.profileId}
              url={user.coverPublicImageUrl}
              aspect='video'
              setUrl={onCustomUploadUrl}
              success={onCustomUploadSuccess}
              didUpload={() => {}}
            />
          </>
        )}

        {activeTab === 'upload' && !isPremium && (
          <div className='rounded-md bg-gradient-to-br from-cccpurple/5 to-cccblue/5 dark:from-cccpurple/10 dark:to-cccblue/10 p-6 text-center'>
            <SparklesIcon className='h-8 w-8 text-yellow-500 mx-auto mb-2' />
            <h4 className='text-base font-semibold text-gray-900 dark:text-gray-100'>A perk of CCC Premium</h4>
            <p className='text-sm text-gray-600 dark:text-gray-300 mt-1 max-w-md mx-auto'>
              Premium members can upload a custom cover photo in any style they like. The preset designs in the other tab are always free and look great too.
            </p>
            <Link
              to='/membership'
              onClick={closeModal}
              className='mt-4 inline-block text-sm font-medium text-cccpurple hover:text-cccpurple-alt hover:underline'
            >
              Learn more about Premium →
            </Link>
          </div>
        )}
      </Modal>
    </div>
  </>
}

export default ProfileGeneralCover
